import React from 'react';
import {
  Box,
  Typography,
  TextField,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  Button,
  Grid,
  Card,
  CardContent,
  Divider,
  InputAdornment,
  Radio,
  RadioGroup,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import {
  SipsAutomationPreferences,
  BucketPreference,
  MinimumAssetsBand,
  TAX_BRACKET_OPTIONS,
  DECREASE_INCOME_PERCENT_OPTIONS,
  ROTH_MAIN,
  ROTH_WHAT_IFS,
  enforceRothInvariant,
  rothOn,
  rothTechniqueSupported
} from '../../../types/automationPreferences';

export const TIME_HORIZON_AGES = [80, 85, 90, 95, 100] as const;

interface AutomationPreferencesFormProps {
  value: Partial<SipsAutomationPreferences>;
  onChange: (next: Partial<SipsAutomationPreferences>) => void;
  disabled?: boolean;
}

/**
 * Controlled presentational form for SIPS automation preferences — the six
 * section Cards, bucket cards, and banded-minimums table. Owns NO data
 * fetching/saving: the per-advisor editor (SipsAutomationPreferencesEditor)
 * and the enterprise/firm-defaults editors wrap it with their own load/save.
 */
const AutomationPreferencesForm: React.FC<AutomationPreferencesFormProps> = ({
  value,
  onChange,
  disabled = false
}) => {
  const handleChange = <K extends keyof SipsAutomationPreferences>(
    field: K,
    fieldValue: SipsAutomationPreferences[K]
  ) => {
    onChange({ ...value, [field]: fieldValue });
  };

  // The main Roth analysis (SC06.01) is the master switch: turning it off also
  // turns off the what-if analyses (enforceRothInvariant — only keys this SIPS
  // returned, so an older release never receives one it would reject on PUT).
  const rothMainOn = rothOn(value);
  const setRothMain = (runs: boolean) => {
    onChange(enforceRothInvariant({ ...value, skipRothConversion: !runs }));
  };

  const handleBucketChange = (index: number, field: keyof BucketPreference, fieldValue: any) => {
    const buckets = [...(value.bucketPreferences || [])];
    buckets[index] = { ...buckets[index], [field]: fieldValue };
    onChange({ ...value, bucketPreferences: buckets });
  };

  const handleBandChange = (index: number, field: keyof MinimumAssetsBand, fieldValue: any) => {
    const bands = [...(value.minimumAssetsBands || [])];
    bands[index] = { ...bands[index], [field]: fieldValue };
    onChange({ ...value, minimumAssetsBands: bands });
  };

  const addBand = () => {
    const bands = [...(value.minimumAssetsBands || [])];
    const nextSequence = bands.length > 0
      ? Math.max(...bands.map(b => b.bandSequence)) + 1
      : 1;
    bands.push({
      bandSequence: nextSequence,
      minimumAssetValue: 0,
      maximumAssetValue: null,
      minimumPercent: 0
    });
    onChange({ ...value, minimumAssetsBands: bands });
  };

  const removeBand = (index: number) => {
    const bands = [...(value.minimumAssetsBands || [])];
    bands.splice(index, 1);
    // Re-sequence
    bands.forEach((band, i) => { band.bandSequence = i + 1; });
    onChange({ ...value, minimumAssetsBands: bands });
  };

  const isBanded = value.minimumAssetsType === 'banded';

  return (
    <fieldset disabled={disabled} style={{ border: 0, padding: 0, margin: 0, minWidth: 0 }}>
      {/* ============================================================ */}
      {/* SECTION 1: Case Settings */}
      {/* ============================================================ */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>Case Settings</Typography>

          {/* Time Horizon */}
          <Typography variant="subtitle2" sx={{ mb: 1 }}>Time Horizon</Typography>
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Client Type</InputLabel>
                <Select
                  value={value.timeHorizonClientType || ''}
                  label="Client Type"
                  onChange={(e) => handleChange('timeHorizonClientType', e.target.value as 'youngest' | 'oldest')}
                >
                  <MenuItem value="youngest">Youngest Client</MenuItem>
                  <MenuItem value="oldest">Oldest Client</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Target Age</InputLabel>
                <Select
                  value={value.timeHorizonAge ?? ''}
                  label="Target Age"
                  onChange={(e) =>
                    handleChange(
                      'timeHorizonAge',
                      e.target.value === '' ? null : Number(e.target.value)
                    )
                  }
                >
                  {value.timeHorizonAge != null &&
                    !TIME_HORIZON_AGES.includes(value.timeHorizonAge as any) && (
                      <MenuItem value={value.timeHorizonAge}>
                        {value.timeHorizonAge} (non-standard)
                      </MenuItem>
                    )}
                  {TIME_HORIZON_AGES.map((age) => (
                    <MenuItem key={age} value={age}>
                      {age}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>Age at end of planning horizon</FormHelperText>
              </FormControl>
            </Grid>
          </Grid>

          <Divider sx={{ mb: 2 }} />

          {/* Savings strategy prior to distributions — "make up surplus income". The stored
              values are unchanged (automatic_makeup / client_defined); only the copy is. */}
          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Make up surplus income</Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            Before distributions begin, should unspent income be deposited into savings?
          </Typography>
          <FormControl component="fieldset">
            <RadioGroup
              row
              value={value.savingsStrategyPriorDistributions || 'automatic_makeup'}
              onChange={(e) => handleChange('savingsStrategyPriorDistributions', e.target.value as 'automatic_makeup' | 'client_defined')}
            >
              <FormControlLabel value="automatic_makeup" control={<Radio />} label="Yes — deposit unspent income into savings (automatic makeup)" />
              <FormControlLabel value="client_defined" control={<Radio />} label="No — no surplus deposits (client defined)" />
            </RadioGroup>
          </FormControl>
        </CardContent>
      </Card>

      {/* ============================================================ */}
      {/* SECTION 2: Income Optimization */}
      {/* ============================================================ */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>Income Optimization</Typography>

          {/* Skip Income Optimization */}
          <FormControlLabel
            control={
              <Switch
                checked={value.skipIncomeOptimization || false}
                onChange={(e) => handleChange('skipIncomeOptimization', e.target.checked)}
              />
            }
            label="Skip Income Optimization?"
          />
          <FormHelperText sx={{ mb: 2 }}>
            Skips the Income Optimization scenario entirely — no Social Security claiming optimization is performed.
          </FormHelperText>

          {/* Social Security */}
          <Typography variant="subtitle2" sx={{ mb: 1 }}>Social Security</Typography>
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth disabled={disabled || value.skipIncomeOptimization}>
                <InputLabel>Optimization Strategy</InputLabel>
                <Select
                  value={value.socialSecurityOptimization || ''}
                  label="Optimization Strategy"
                  onChange={(e) => handleChange('socialSecurityOptimization', e.target.value as 'maximize_savings' | 'file_at_age')}
                >
                  <MenuItem value="maximize_savings">Maximize Savings</MenuItem>
                  <MenuItem value="file_at_age">Always File at Age</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="File at Age"
                type="number"
                value={value.socialSecurityFileAtAge ?? ''}
                onChange={(e) => handleChange('socialSecurityFileAtAge', parseInt(e.target.value) || null)}
                disabled={disabled || value.skipIncomeOptimization || value.socialSecurityOptimization !== 'file_at_age'}
                helperText="Only applicable when using 'Always File at Age'"
                inputProps={{ min: 62, max: 70 }}
              />
            </Grid>
          </Grid>

          <Divider sx={{ mb: 2 }} />

          {/* Age to Decrease Income */}
          <Typography variant="subtitle2" sx={{ mb: 1 }}>Age to Decrease Income</Typography>
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                label="Age to Decrease Income"
                type="number"
                value={value.ageToDecreaseIncome ?? ''}
                onChange={(e) => handleChange('ageToDecreaseIncome', parseInt(e.target.value) || null)}
                inputProps={{ min: 50, max: 120 }}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <FormControl fullWidth>
                <InputLabel>Percent to Decrease</InputLabel>
                <Select
                  value={value.percentToDecreaseIncome ?? ''}
                  label="Percent to Decrease"
                  onChange={(e) => handleChange('percentToDecreaseIncome', e.target.value === '' ? null : e.target.value as number)}
                >
                  <MenuItem value="">-- None --</MenuItem>
                  {DECREASE_INCOME_PERCENT_OPTIONS.map(option => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={3}>
              <FormControl component="fieldset">
                <Typography variant="caption" color="textSecondary" sx={{ mb: 0.5 }}>Which Client</Typography>
                <RadioGroup
                  row
                  value={value.whichClientDecreaseIncome || ''}
                  onChange={(e) => handleChange('whichClientDecreaseIncome', e.target.value as 'oldest' | 'youngest')}
                >
                  <FormControlLabel value="oldest" control={<Radio size="small" />} label="Oldest" />
                  <FormControlLabel value="youngest" control={<Radio size="small" />} label="Youngest" />
                </RadioGroup>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={3}>
              <FormControl component="fieldset">
                <Typography variant="caption" color="textSecondary" sx={{ mb: 0.5 }}>When to Apply</Typography>
                <RadioGroup
                  value={value.whenToApplyDecreaseIncome || ''}
                  onChange={(e) => handleChange('whenToApplyDecreaseIncome', e.target.value as 'all' | 'needed' | 'never')}
                >
                  <FormControlLabel value="all" control={<Radio size="small" />} label="All plans, every time" />
                  <FormControlLabel value="needed" control={<Radio size="small" />} label="Only when needed" />
                  <FormControlLabel value="never" control={<Radio size="small" />} label="Never" />
                </RadioGroup>
              </FormControl>
            </Grid>
          </Grid>

          <Divider sx={{ mb: 2 }} />

          {/* Age to Limit Inflation */}
          <Typography variant="subtitle2" sx={{ mb: 1 }}>Age to Limit Inflation</Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Age to Limit Inflation"
                type="number"
                value={value.ageToLimitInflation ?? ''}
                onChange={(e) => handleChange('ageToLimitInflation', parseInt(e.target.value) || null)}
                inputProps={{ min: 50, max: 100 }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl component="fieldset">
                <Typography variant="caption" color="textSecondary" sx={{ mb: 0.5 }}>When to Apply</Typography>
                <RadioGroup
                  value={value.whenToApplyInflationLimit || ''}
                  onChange={(e) => handleChange('whenToApplyInflationLimit', e.target.value as 'all' | 'needed' | 'never')}
                >
                  <FormControlLabel value="all" control={<Radio size="small" />} label="All plans, every time" />
                  <FormControlLabel value="needed" control={<Radio size="small" />} label="Only when needed" />
                  <FormControlLabel value="never" control={<Radio size="small" />} label="Never" />
                </RadioGroup>
              </FormControl>
            </Grid>
          </Grid>

          <Divider sx={{ my: 2 }} />

          {/* Earliest Plan Year to Adjust Income */}
          <Typography variant="subtitle2" sx={{ mb: 1 }}>Earliest Plan Year to Adjust Income</Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Earliest Plan Year to Adjust Income"
                type="number"
                value={value.minimumPlanYearBeforeAdjustments ?? ''}
                onChange={(e) => handleChange('minimumPlanYearBeforeAdjustments', parseInt(e.target.value) || null)}
                helperText="Applies to both the decrease-income and inflation-limit adjustments above; blank or 0 means no floor"
                inputProps={{ min: 0, max: 999 }}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* ============================================================ */}
      {/* SECTION 3: Asset Allocation Strategy */}
      {/* ============================================================ */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>Asset Allocation Strategy</Typography>

          <FormHelperText sx={{ mb: 2 }}>
            Asset allocation always runs. Bucket years define each account's time horizon; the return
            assumptions below drive growth projections and shape Roth conversion funding.
          </FormHelperText>

          <Typography variant="subtitle2" sx={{ mb: 1 }}>Bucket Configuration</Typography>
          <Grid container spacing={2}>
            {(value.bucketPreferences || []).map((bucket, index) => (
              <Grid item xs={12} sm={6} md={3} key={bucket.bucketType}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle2" gutterBottom color="primary">
                      {bucket.bucketName}
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                    <Grid container spacing={1}>
                      <Grid item xs={6}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Start Year"
                          type="number"
                          value={bucket.startYear ?? ''}
                          onChange={(e) => handleBucketChange(index, 'startYear', parseInt(e.target.value) || null)}
                          disabled={disabled || bucket.bucketType === 'discretionary'}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <TextField
                          fullWidth
                          size="small"
                          label="End Year"
                          type="number"
                          value={bucket.endYear ?? ''}
                          onChange={(e) => handleBucketChange(index, 'endYear', parseInt(e.target.value) || null)}
                          disabled={disabled || bucket.bucketType === 'longTerm' || bucket.bucketType === 'discretionary'}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Return"
                          type="number"
                          value={bucket.returnPercentage ?? ''}
                          onChange={(e) => handleBucketChange(index, 'returnPercentage', parseFloat(e.target.value) || 0)}
                          InputProps={{
                            endAdornment: <InputAdornment position="end">%</InputAdornment>
                          }}
                          inputProps={{ step: 0.1, min: 0, max: 20 }}
                        />
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </CardContent>
      </Card>

      {/* ============================================================ */}
      {/* SECTION 4: Target Income Optimization */}
      {/* ============================================================ */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>Target Income Optimization</Typography>

          {/* Minimum Assets Type */}
          <Typography variant="subtitle2" sx={{ mb: 1 }}>Minimum Assets Type</Typography>
          <FormControl component="fieldset" sx={{ mb: 2 }}>
            <RadioGroup
              row
              value={value.minimumAssetsType || 'fixed'}
              onChange={(e) => handleChange('minimumAssetsType', e.target.value as 'fixed' | 'banded')}
            >
              <FormControlLabel value="fixed" control={<Radio />} label="Fixed Percentage" />
              <FormControlLabel value="banded" control={<Radio />} label="Banded by Asset Value" />
            </RadioGroup>
          </FormControl>

          {/* Fixed Minimum % */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                label="Minimum End-of-Plan Assets"
                type="number"
                value={value.minimumEndOfPlanAssets ?? ''}
                onChange={(e) => handleChange('minimumEndOfPlanAssets', parseInt(e.target.value) || null)}
                InputProps={{
                  endAdornment: <InputAdornment position="end">%</InputAdornment>
                }}
                inputProps={{ min: 0, max: 100 }}
                disabled={disabled || isBanded}
              />
            </Grid>
          </Grid>

          {/* Banded Minimums */}
          {isBanded && (
            <>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Minimum Assets Bands</Typography>
              <TableContainer sx={{ mb: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>#</TableCell>
                      <TableCell>Min Asset Value ($)</TableCell>
                      <TableCell>Max Asset Value ($)</TableCell>
                      <TableCell>Min %</TableCell>
                      <TableCell width={50}></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(value.minimumAssetsBands || []).map((band, index) => (
                      <TableRow key={band.bandSequence}>
                        <TableCell>{band.bandSequence}</TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            type="number"
                            value={band.minimumAssetValue ?? ''}
                            onChange={(e) => handleBandChange(index, 'minimumAssetValue', parseFloat(e.target.value) || 0)}
                            inputProps={{ min: 0 }}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            type="number"
                            value={band.maximumAssetValue ?? ''}
                            onChange={(e) => handleBandChange(index, 'maximumAssetValue', e.target.value === '' ? null : parseFloat(e.target.value))}
                            placeholder="Unlimited"
                            inputProps={{ min: 0 }}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            type="number"
                            value={band.minimumPercent ?? ''}
                            onChange={(e) => handleBandChange(index, 'minimumPercent', parseInt(e.target.value) || 0)}
                            InputProps={{
                              endAdornment: <InputAdornment position="end">%</InputAdornment>
                            }}
                            inputProps={{ min: 0, max: 100 }}
                          />
                        </TableCell>
                        <TableCell>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => removeBand(index)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <Button
                size="small"
                startIcon={<AddIcon />}
                onClick={addBand}
              >
                Add Band
              </Button>
            </>
          )}

          <Divider sx={{ my: 2 }} />

          {/* Min Assets Floor Calculation */}
          <Typography variant="subtitle2" sx={{ mb: 1 }}>Minimum Assets Floor Calculation</Typography>
          <FormControl component="fieldset">
            <RadioGroup
              row
              value={value.minimumAssetsCalculationMethod || 'sum'}
              onChange={(e) => handleChange('minimumAssetsCalculationMethod', e.target.value as 'sum' | 'max')}
            >
              <FormControlLabel value="sum" control={<Radio />} label="Sum (Floor + Legacy)" />
              <FormControlLabel value="max" control={<Radio />} label="Maximum (Larger of Two)" />
            </RadioGroup>
          </FormControl>
        </CardContent>
      </Card>

      {/* ============================================================ */}
      {/* SECTION 5: Roth Conversion */}
      {/* ============================================================ */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>Roth Conversion</Typography>
          <FormHelperText sx={{ mb: 2 }}>
            Roth conversions run the Maximize Assets analysis (the one plan results report). Two optional
            what-if analyses can run alongside it; they're switched off whenever Roth conversions are off.
            Each bracket caps the highest marginal bracket that analysis converts into.
          </FormHelperText>
          <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
            <Grid item xs={12} md={7}>
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={rothMainOn}
                    onChange={(e) => setRothMain(e.target.checked)}
                    disabled={disabled}
                  />
                }
                label={
                  <Box>
                    <Typography variant="body2">Run Roth conversions ({ROTH_MAIN.label})</Typography>
                    <Typography variant="caption" color="text.secondary">{ROTH_MAIN.desc}</Typography>
                  </Box>
                }
              />
            </Grid>
            <Grid item xs={12} md={5}>
              <FormControl fullWidth size="small" disabled={disabled || !rothMainOn}>
                <InputLabel shrink>Max Conversion Tax Bracket</InputLabel>
                <Select
                  label="Max Conversion Tax Bracket"
                  displayEmpty
                  notched
                  value={value[ROTH_MAIN.bracketKey] ?? ''}
                  onChange={(e) =>
                    handleChange(ROTH_MAIN.bracketKey, e.target.value === '' ? null : Number(e.target.value))
                  }
                >
                  <MenuItem value="">-- Select --</MenuItem>
                  {TAX_BRACKET_OPTIONS.map(option => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
          <Typography variant="subtitle2" gutterBottom>Optional what-if analyses</Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Technique</TableCell>
                  <TableCell align="center" width={90}>Run</TableCell>
                  <TableCell width={260}>Max Conversion Tax Bracket</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {ROTH_WHAT_IFS.map((technique) => {
                  // A SIPS release before PD-248 has no key for the what-if analyses
                  // (and rejects them on PUT) — show the row, but inert.
                  const supported = rothTechniqueSupported(value, technique);
                  const runs = rothMainOn && supported && value[technique.skipKey] !== true;
                  return (
                    <TableRow key={technique.id}>
                      <TableCell>
                        <Typography variant="body2">{technique.label}</Typography>
                        <Typography variant="caption" color="text.secondary" component="div">{technique.desc}</Typography>
                        {!supported && (
                          <Typography variant="caption" color="text.disabled" component="div">
                            Requires the latest SIPS release
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <Switch
                          size="small"
                          checked={runs}
                          onChange={(e) => handleChange(technique.skipKey, !e.target.checked)}
                          disabled={disabled || !rothMainOn || !supported}
                        />
                      </TableCell>
                      <TableCell>
                        <FormControl fullWidth size="small" disabled={disabled || !runs}>
                          <Select
                            displayEmpty
                            value={value[technique.bracketKey] ?? ''}
                            onChange={(e) =>
                              handleChange(
                                technique.bracketKey,
                                e.target.value === '' ? null : Number(e.target.value)
                              )
                            }
                          >
                            <MenuItem value="">-- Select --</MenuItem>
                            {TAX_BRACKET_OPTIONS.map(option => (
                              <MenuItem key={option.value} value={option.value}>
                                {option.label}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* ============================================================ */}
      {/* SECTION 5b: Medicare IRMAA (SIPS PD-281) */}
      {/* ============================================================ */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>Medicare IRMAA</Typography>
          <FormControlLabel
            control={
              <Switch
                checked={value.modelIrmaaExpenses === true}
                onChange={(e) => handleChange('modelIrmaaExpenses', e.target.checked)}
                disabled={disabled}
              />
            }
            label="Model IRMAA Expenses"
          />
          <FormHelperText>
            Adds each client's Medicare IRMAA surcharge (Part B and Part D) to every scenario from age 65, assessed on the
            household's MAGI from two years earlier. Needs Advanced Tax Planning in SIPS. On by default; any plan run can
            switch it off from the Medicare IRMAA card.
          </FormHelperText>
        </CardContent>
      </Card>

      {/* ============================================================ */}
      {/* SECTION 6: Plan Resiliency */}
      {/* ============================================================ */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>Plan Resiliency</Typography>

          {/* Survivorship Analysis (SC06.16) */}
          <Typography variant="subtitle2" sx={{ mb: 1 }}>Survivorship Analysis</Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <FormControl component="fieldset">
                <Typography variant="caption" color="textSecondary" sx={{ mb: 0.5 }}>When to Run Survivorship Analysis</Typography>
                <RadioGroup
                  value={value.whenToRunSurvivorshipAnalysis || 'never'}
                  onChange={(e) => handleChange('whenToRunSurvivorshipAnalysis', e.target.value as 'never' | 'always' | 'when_needed')}
                >
                  <FormControlLabel value="always" control={<Radio size="small" />} label="For all plans, every time" />
                  <FormControlLabel value="when_needed" control={<Radio size="small" />} label="Only if a pension's survivorship is at or below the threshold" />
                  <FormControlLabel value="never" control={<Radio size="small" />} label="Never" />
                </RadioGroup>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                label="Pension Survivorship Threshold"
                type="number"
                value={value.pensionSurvivorshipThresholdPercent ?? ''}
                onChange={(e) => handleChange('pensionSurvivorshipThresholdPercent', parseInt(e.target.value) || null)}
                disabled={disabled || value.whenToRunSurvivorshipAnalysis !== 'when_needed'}
                helperText="Only applicable with the threshold option"
                InputProps={{
                  endAdornment: <InputAdornment position="end">%</InputAdornment>
                }}
                inputProps={{ min: 0, max: 100 }}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                label="Life Expectancy Age"
                type="number"
                value={value.lifeExpectancyAgeForSurvivorshipAnalysis ?? ''}
                onChange={(e) => handleChange('lifeExpectancyAgeForSurvivorshipAnalysis', parseInt(e.target.value) || null)}
                helperText="Life expectancy age used for the survivorship analysis"
                inputProps={{ min: 50, max: 120 }}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* ============================================================ */}
      {/* SECTION 7: Presentation */}
      {/* ============================================================ */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>Presentation</Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                label="Advisory Fee"
                type="number"
                value={value.advisoryFee ?? ''}
                onChange={(e) => handleChange('advisoryFee', parseFloat(e.target.value) || null)}
                InputProps={{
                  endAdornment: <InputAdornment position="end">%</InputAdornment>
                }}
                inputProps={{ step: 0.1, min: 0, max: 100 }}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </fieldset>
  );
};

export default AutomationPreferencesForm;
